import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";
import { generateUsernameSuggestions } from "../../utils/usernameGenerator";
import { useUsernameValidation } from "../../hooks/user/useUsernameValidation";
import { useUpdateUsername } from "../../hooks/user/useUpdateUsername";

const UsernameSelectionModal = ({ isOpen, user, onComplete }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [customInput, setCustomInput] = useState("");
  const [submissionError, setSubmissionError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Use validation hook for real-time validation
  const { validateUsername, isValidating, validationError, isValid } =
    useUsernameValidation();

  // Use update username hook for submission
  const { mutate: updateUsername, isLoading: isSubmitting } =
    useUpdateUsername();

  // Generate suggestions on modal mount
  useEffect(() => {
    if (isOpen && user) {
      const generatedSuggestions = generateUsernameSuggestions(user);
      setSuggestions(generatedSuggestions);
    }
  }, [isOpen, user]);

  // Handle suggestion click - populate input field
  const handleSuggestionClick = (suggestion) => {
    setSelectedUsername(suggestion);
    setCustomInput(suggestion);
    setSubmissionError(null); // Clear submission error when selecting suggestion
    setRetryCount(0); // Reset retry count when selecting suggestion
    validateUsername(suggestion);
  };

  // Handle custom input change
  const handleCustomInputChange = (e) => {
    const value = e.target.value;
    setCustomInput(value);
    setSelectedUsername(value);
    setSubmissionError(null); // Clear submission error when user types
    setRetryCount(0); // Reset retry count when user changes input
    validateUsername(value);
  };

  // Handle confirm button click
  const handleConfirm = () => {
    if (!isValid || !selectedUsername || isSubmitting) {
      return;
    }

    setSubmissionError(null);
    setRetryCount(0); // Reset retry count on new submission

    updateUsername(selectedUsername, {
      onSuccess: () => {
        // Reset retry count on success
        setRetryCount(0);
        // Close modal and trigger onComplete callback
        if (onComplete) {
          onComplete();
        }
      },
      onError: (error) => {
        // Increment retry count
        setRetryCount((prev) => prev + 1);

        // Handle errors - display detailed error message and allow retry
        let errorMessage = "Failed to set username. Please try again.";
        let errorDetails = null;

        if (error.code === "ECONNABORTED") {
          errorMessage = "Request timed out";
          errorDetails = "Please check your internet connection and try again.";
        } else if (error.response) {
          const status = error.response.status;
          const serverMessage =
            error.response?.data?.errors?.[0]?.msg ||
            error.response?.data?.message;

          if (status === 400) {
            errorMessage = "Invalid username";
            errorDetails =
              serverMessage ||
              "The username format is invalid. Please try a different username.";
          } else if (status === 409) {
            errorMessage = "Username taken";
            errorDetails =
              "This username was just taken by another user. Please choose a different one.";
          } else if (status === 401 || status === 403) {
            errorMessage = "Authentication error";
            errorDetails =
              "Your session may have expired. Please refresh the page and try again.";
          } else if (status >= 500) {
            errorMessage = "Server error";
            errorDetails =
              "Our servers are experiencing issues. Please try again in a moment.";
          } else if (serverMessage) {
            errorMessage = serverMessage;
          }
        } else if (error.request) {
          errorMessage = "Network error";
          errorDetails =
            "Unable to reach the server. Please check your internet connection.";
        }

        setSubmissionError({ message: errorMessage, details: errorDetails });
      },
    });
  };

  // Determine if confirm button should be disabled
  const isConfirmDisabled =
    !selectedUsername || !isValid || isValidating || isSubmitting;

  return (
    <Dialog
      open={isOpen}
      onClose={() => {}} // Non-dismissible - empty function prevents closing
      className="relative z-50"
    >
      {/* Backdrop - non-clickable */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      {/* Full-screen container to center the modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
          {/* Header with Logo and Title */}
          <div className="bg-gradient-to-r from-deep-red to-bright-red text-white p-6 text-center">
            <div className="flex justify-center mb-4">
              <img
                src="/emblem-gold.png"
                alt="DayShift Sports"
                className="h-16 w-16"
              />
            </div>
            <Dialog.Title className="text-2xl font-bold mb-2">
              Choose Your Fighter Name
            </Dialog.Title>
            <Dialog.Description className="text-white/90 text-sm">
              Set a unique username to compete in the global leaderboard
            </Dialog.Description>
          </div>

          {/* Content Area */}
          <div className="p-6 bg-white">
            {/* Username Suggestions */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-medium-grey mb-3">
                Quick suggestions:
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`
                      px-4 py-3 rounded-lg border-2 text-base font-medium
                      transition-all duration-150 ease-in-out
                      ${
                        selectedUsername === suggestion
                          ? "border-deep-red bg-deep-red/5 text-deep-red"
                          : "border-gray-200 bg-gray-50 text-dark-grey hover:border-deep-red/50 hover:bg-gray-100 hover:scale-105"
                      }
                      focus:outline-none focus:ring-2 focus:ring-deep-red focus:ring-offset-2
                      active:scale-95
                    `}
                    aria-label={`Select username ${suggestion}`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Username Input */}
            <div className="mb-6">
              <label
                htmlFor="custom-username"
                className="block text-sm font-medium text-medium-grey mb-2"
              >
                Or create your own:
              </label>
              <div className="relative">
                <input
                  id="custom-username"
                  type="text"
                  value={customInput}
                  onChange={handleCustomInputChange}
                  placeholder="Enter your username..."
                  className={`
                    w-full px-4 py-3 pr-12 rounded-lg border-2 text-base
                    transition-all duration-150 ease-in-out
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    ${
                      validationError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : isValid
                        ? "border-green-500 focus:border-green-500 focus:ring-green-500"
                        : "border-gray-300 focus:border-deep-red focus:ring-deep-red"
                    }
                  `}
                  aria-invalid={validationError ? "true" : "false"}
                  aria-describedby={
                    validationError
                      ? "username-error"
                      : isValid
                      ? "username-success"
                      : undefined
                  }
                />

                {/* Visual Indicators */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isValidating && (
                    <div className="animate-spin h-5 w-5 border-2 border-deep-red border-t-transparent rounded-full" />
                  )}
                  {!isValidating && isValid && (
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-label="Username available"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {!isValidating && validationError && (
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-label="Username error"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Validation Feedback Messages */}
              {validationError && (
                <p
                  id="username-error"
                  className="mt-2 text-sm text-red-600 flex items-start"
                  role="alert"
                >
                  <svg
                    className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {validationError}
                </p>
              )}
              {!validationError && isValid && (
                <p
                  id="username-success"
                  className="mt-2 text-sm text-green-600 flex items-center"
                >
                  <svg
                    className="h-4 w-4 mr-1 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Username available
                </p>
              )}
              {!validationError && !isValid && customInput && !isValidating && (
                <p className="mt-2 text-sm text-medium-grey">
                  Enter 3-20 characters (letters, numbers, _ or -)
                </p>
              )}
            </div>

            {/* Submission Error Message */}
            {submissionError && (
              <div
                className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200"
                role="alert"
                aria-live="assertive"
              >
                <div className="flex items-start">
                  <svg
                    className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800">
                      {typeof submissionError === "string"
                        ? submissionError
                        : submissionError.message}
                    </p>
                    {submissionError.details && (
                      <p className="text-sm text-red-700 mt-1">
                        {submissionError.details}
                      </p>
                    )}
                    {retryCount > 0 && (
                      <p className="text-xs text-red-600 mt-2">
                        Retry attempt {retryCount} of 2
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Confirm Button */}
            <button
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              className={`
                w-full py-3 px-6 rounded-lg text-base font-bold
                transition-all duration-150 ease-in-out
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deep-red
                ${
                  isConfirmDisabled
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-deep-red text-white hover:bg-bright-red active:scale-95 shadow-lg hover:shadow-xl"
                }
              `}
              aria-label="Confirm username selection"
              aria-disabled={isConfirmDisabled}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Setting Username...
                </span>
              ) : (
                "Confirm Username"
              )}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default UsernameSelectionModal;
